import React, { useEffect, useRef, useState } from 'react';
import toggleStickyHeader from 'utils/toggleStickyHeader';
import useIsScrolled from 'hooks/useIsScrolled';
import SimpleHeader from 'components/Header/SimpleHeader';
import WebsiteHeader from 'components/Header/WebsiteHeader';
import { ProgressBar } from 'scrolling-based-progressbar';
import useExternalBanner from 'hooks/useExternalBanner';
import useWindowDimensions from 'hooks/useWindowDimensions';
import sweetScroll, { updateHeaderOption } from 'utils/sweetScroll';
import { HeaderContainer, HorizontalProgressBar } from './styles';

const HeaderWrapper = ({
  hasAnchorLinkNavigation,
  header = {},
  simpleHeader,
  stickyHeader,
  noHero,
  SubNav,
  toggleHeaderType = () => {},
  toggleIsStickyWithHeader = () => {},
  toggleIsStickyWithoutHeader = () => {},
  useProgressbar,
  theme,
}) => {
  const ref = useRef(null);
  const [displaySimpleHeader, toggleDisplaySimpleHeader] = useState(!!simpleHeader);
  const [displayMainHeader, toggleDisplayMainHeader] = useState(!!header);
  const { isStickyWithHeader, isStickyWithoutHeader } = toggleStickyHeader(
    ref,
    toggleDisplaySimpleHeader
  );
  const isStickyHeader = stickyHeader || isStickyWithHeader || isStickyWithoutHeader;
  const { bannerHeight } = useExternalBanner();

  const { isScrolled } = useIsScrolled();
  const {
    announcementBarCopy,
    announcementBarLinkTitle,
    announcementBarLinkUrl,
    hideAnnouncementBarSignIn,
    customPhoneNumber,
    landingPage,
    landingPageMenuItems,
    logoRoute,
    logoType,
    navigation,
    showMainMenuItems,
    mobileUseGlobalNav,
  } = header;

  const announcementBar = {
    copy: announcementBarCopy,
    linkTitle: announcementBarLinkTitle,
    linkUrl: announcementBarLinkUrl,
    hideSignIn: hideAnnouncementBarSignIn,
  };

  const contextualCta = header.contextualCta && header.contextualCta[0];
  const windowWidth = useWindowDimensions().innerWidth;
  const [scroll, setScroll] = useState(null);

  useEffect(() => {
    if (isStickyWithHeader && !isScrolled) {
      toggleDisplayMainHeader(true);
    } else if (isStickyWithHeader) {
      toggleDisplayMainHeader(true);
    } else if (isStickyWithoutHeader) {
      if (!simpleHeader) toggleDisplayMainHeader(false);
    }
  }, [isStickyWithHeader, isStickyWithoutHeader, isScrolled]);

  useEffect(() => {
    toggleIsStickyWithHeader(isStickyWithHeader);
    toggleIsStickyWithoutHeader(isStickyWithoutHeader);
    toggleHeaderType({ simpleHeader: displaySimpleHeader, mainHeader: displayMainHeader });

    if (!scroll) {
      setScroll(sweetScroll());
    } else {
      updateHeaderOption(scroll);
    }
  }, [isStickyHeader, displaySimpleHeader, displayMainHeader, windowWidth]);

  return (
    <HeaderContainer
      bannerData={{ scrolled: isScrolled, height: bannerHeight }}
      stickyHeader={isStickyHeader}
      data-testid="header-wrapper"
      displaySimpleHeader={displaySimpleHeader}
      simpleHeader={simpleHeader}
      className="header-container"
    >
      <div ref={simpleHeader ? ref : null}>
        {displaySimpleHeader && simpleHeader ? (
          <SimpleHeader
            toggleDisplaySimpleHeader={toggleDisplaySimpleHeader}
            toggleDisplayMainHeader={toggleDisplayMainHeader}
          />
        ) : null}
        {displayMainHeader ? (
          <WebsiteHeader
            mobileUseGlobalNav={mobileUseGlobalNav}
            announcementBar={announcementBar}
            contextualCta={contextualCta}
            customPhoneNumber={customPhoneNumber}
            hasAnchorLinkNavigation={hasAnchorLinkNavigation}
            landingPage={landingPage}
            landingPageMenuItems={landingPageMenuItems}
            logoRoute={simpleHeader ? '/' : logoRoute}
            logoType={logoType}
            navigation={navigation}
            showMainMenuItems={showMainMenuItems}
            simpleHeader={simpleHeader}
            stickyHeader={stickyHeader}
            noHero={noHero}
            theme={theme}
          />
        ) : null}
      </div>
      {SubNav && (
        <div ref={simpleHeader ? null : ref}>
          <SubNav isScrolled={isScrolled} />
        </div>
      )}
      {useProgressbar && (
        <HorizontalProgressBar role="progress-bar" isScrolled={isScrolled}>
          <div />
          <ProgressBar
            height="4px"
            color="var(--secondary-color)"
            bgColor="transparent"
            left="10%"
            bottom="-4px"
            zIndex="999"
            postition="absolute"
          />
        </HorizontalProgressBar>
      )}
    </HeaderContainer>
  );
};

export default HeaderWrapper;
